import { useEffect, useRef } from "react";
import {
  STAGE_ROW_KEY,
  ELAPSED_CACHE_KEY,
  ensureStageRowId,
  ensureStageSessionForUser,
  readGameStageRecord,
  saveStageTimeOnly,
} from "../../functions/stageReportSync";

/** Clock value last sent to the DB, and the save still in flight (if any). */
let lastSavedTime = null;
let pendingTimeSave = null;

function readCachedElapsed() {
  try {
    const n = Number(sessionStorage.getItem(ELAPSED_CACHE_KEY));
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

export function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

export function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

/** Live HUD progress for the current stage: round((correct/total) * stagePoints). */
export function liveProgressPoints(correctCount, totalCount, stagePoints) {
  const total = Number(totalCount) || 0;
  if (total <= 0) return 0;
  const n = Math.max(0, Math.min(Number(correctCount) || 0, total));
  return Math.round((n / total) * (Number(stagePoints) || 0));
}

/** Read cumulative `total_score` from a stages row (fallback: sum stage1…stage10). */
export function readTotalScoreFromStage(stage) {
  if (!stage) return 0;
  if (stage.total_score != null && stage.total_score !== "") {
    const n = Number(stage.total_score);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  let sum = 0;
  for (let i = 1; i <= 10; i += 1) {
    const v = Number(stage[`stage${i}`]);
    if (Number.isFinite(v) && v > 0) sum += v;
  }
  return sum;
}

/**
 * Load stages.time from DB (creates row if needed). Returns { stageId, elapsedSec, stage, error }.
 * Waits for a save from the previous page, and never goes below this tab's own
 * latest value, so the timer cannot jump backwards.
 *
 * `error: true` means the saved progress could not be read (after retries). The
 * page must not start the game then: starting would treat a returning player
 * as new, from 00:00, and save that over their real time and answers.
 */
export async function loadHallElapsedFromDb({
  backendBase,
  adminToken,
  storedUser,
  isDemoBypass,
}) {
  if (isDemoBypass || !backendBase || !adminToken) {
    return { stageId: "", elapsedSec: 0, stage: null };
  }

  ensureStageSessionForUser(storedUser);
  let stageId = null;
  for (let attempt = 1; attempt <= 3 && !stageId; attempt += 1) {
    if (attempt > 1) await new Promise((r) => setTimeout(r, 700 * (attempt - 1)));
    stageId = await ensureStageRowId({
      backendBase,
      adminToken,
      storedUser,
      isDemoBypass,
    });
  }
  if (!stageId) return { stageId: "", elapsedSec: 0, stage: null, error: true };

  // Read after the previous page's save, but never wait on it for long.
  if (pendingTimeSave) {
    await Promise.race([pendingTimeSave, new Promise((r) => setTimeout(r, 3000))]);
  }

  const { stage, error, missing } = await readGameStageRecord({
    backendBase,
    adminToken,
    storedUser,
    stageId,
  });
  if (error || missing || !stage) {
    return { stageId: String(stageId), elapsedSec: 0, stage: null, error: true };
  }

  const dbSec = parseClockToSeconds(stage?.time);
  lastSavedTime = formatSecondsToClock(dbSec);

  return {
    stageId: String(stageId),
    elapsedSec: Math.max(dbSec, readCachedElapsed()),
    stage,
  };
}

/** Persist only the shared timer (DB only; skipped when the value is unchanged). */
export function persistHallElapsedTime({
  backendBase,
  adminToken,
  storedUser,
  isDemoBypass,
  stageId,
  elapsedSec,
}) {
  if (isDemoBypass || !backendBase || !adminToken) return;
  try {
    sessionStorage.setItem(ELAPSED_CACHE_KEY, String(elapsedSec));
  } catch {
    /* storage unavailable: the DB save below still runs */
  }
  const id = stageId || sessionStorage.getItem(STAGE_ROW_KEY);
  const time = formatSecondsToClock(elapsedSec);
  if (!id || time === lastSavedTime) return;

  lastSavedTime = time;
  const save = saveStageTimeOnly({ backendBase, adminToken, storedUser, stageId: id, time }).then((ok) => {
    if (!ok && lastSavedTime === time) lastSavedTime = null; // allow a retry on the next exit
    if (pendingTimeSave === save) pendingTimeSave = null;
  });
  pendingTimeSave = save;
}

/**
 * Saves the timer only when the player leaves: tab hidden or closed, refresh, or
 * moving to another page. Score and time are saved on stage completion separately.
 * `active` is false while the timer is stopped or the level is being saved.
 */
export function useHallTimeSaver({
  active,
  elapsedRef,
  backendBase,
  adminToken,
  storedUser,
  isDemoBypass,
  stageId,
}) {
  const saveRef = useRef(null);
  saveRef.current = active
    ? () =>
        persistHallElapsedTime({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          elapsedSec: elapsedRef.current,
        })
    : null;

  useEffect(() => {
    const save = () => saveRef.current?.();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") save();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", save);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", save);
      save();
    };
  }, []);
}
